import Multiselect from 'multiselect-react-dropdown';
import { useEffect, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { FaAngleDown, FaAngleLeft, FaAngleRight, FaAngleUp, FaEdit, FaTimesCircle, FaTrash } from 'react-icons/fa';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import BulkEditActivitiesModal from '../components/BulkEditActivitiesModal';
import MultiselectWithSelectAll from '../components/MultiselectWithSelectAll';
import PermissionGuard from '../components/PermissionGuard';
import TableSkeleton from '../components/TableSkeleton';
import { getPageCache, setPageCache } from '../utils/pageCache';
import { PERMISSIONS } from '../constants/permissions';
import {
    FORM_SECTION_TITLE_STYLE,
    MULTISELECT_COMMON_PROPS,
    MULTISELECT_THEME_STYLE,
} from '../constants/multiselectTheme';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import './Activities.css';
import { GENERAL_CATEGORY_OPTIONS } from '../constants/categories';
import StaffTypeAbbr from '../components/StaffTypeAbbr';
import { useStaffTypes } from '../hooks/useStaffTypes';

// Add minutes formatting function to show 2 decimal places
const formatMinutes = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toFixed(2);
};

function Activities() {
    const [AddActivity, setAddActivity] = useState(false);
    const [activities, setActivities] = useState([]);
    const [filteredActivities, setFilteredActivities] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSites, setSelectedSites] = useState([]);
    const [availableSites, setAvailableSites] = useState([]);
    const [newActivitySites, setNewActivitySites] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [collapsedRows, setCollapsedRows] = useState({});
    const [customerCareTypes, setCustomerCareTypes] = useState([])
    const [isLoading, setIsLoading] = useState(true);
    const { currentTenant } = useTenant();
    const { getAbbreviation, getDisplayName } = useStaffTypes();
    const { hasPermission } = usePermissions();
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [isBulkEditing, setIsBulkEditing] = useState(false);
    const [bulkEditProgress, setBulkEditProgress] = useState({ done: 0, total: 0 });
    const [newActivity, setNewActivity] = useState({
        activity_name: '',
        customer_care_type_id: '',
        general_category: '',
        frequency: '',
        sn_hours_day: '',
        sn_hours_night: '',
        ena_hours_day: '',
        ena_hours_night: '',
        cw_hours_day: '',
        cw_hours_night: '',
        benefit_body: '',
        benefit_mind: '',
        benefit_social: '',
        date_from: '',
        date_to: '',
    });

    useEffect(() => {
        if (currentTenant?.id) {
            try {
                // Stale-while-revalidate: if we already loaded this tenant this
                // session, show the cached table instantly and refresh silently.
                const cached = getPageCache(`activities:${currentTenant.id}`);
                if (cached) {
                    setActivities(cached.activities);
                    setAvailableSites(cached.sites);
                    filterActivities(searchTerm, selectedSites, cached.activities);
                    setIsLoading(false);
                    fetchActivities({ background: true });
                } else {
                    fetchActivities();
                }
                fetchCustomerCareTypes();
            } catch (error) {
                console.error('Error in useEffect:', error);
            }
        } else {
            setActivities([]);
            setFilteredActivities([]);
            setIsLoading(false);
        }
    }, [currentTenant?.id]);

    const fetchCustomerCareTypes = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/customerCareTypes`);
            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data)) {
                    const updatedCareTypes = data.map(element => ({
                        name: element.Name || element.name,
                        id: element.CareTypeID || element.care_type_id
                    }));

                    setCustomerCareTypes(updatedCareTypes);
                } else {
                    console.error("Error: Expected an array but received", data);
                }

            } else {
                console.error('Failed to fetch customer care types');
            }
        } catch (error) {
            console.error('An error occurred while fetching customer care types:', error);
        }
    };

    const fetchSites = async () => {
        if (!currentTenant) {
            setAvailableSites([]);
            return;
        }

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/sites?tenant_id=${currentTenant.id}`);
            if (response.ok) {
                const data = await response.json();
                // Map the backend response to match the expected format
                const mappedSites = data.map(site => ({
                    site_id: site.id || site.site_id,
                    site_name: site.name || site.site_name
                }));
                setAvailableSites(mappedSites);
            } else {
                console.error('Failed to fetch sites');
            }
        } catch (error) {
            console.error('Error fetching sites:', error);
        }
    };

    const handleSort = (key, isDate = false) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }

        const sortedData = [...filteredActivities].sort((a, b) => {
            if (isDate) {
                return direction === 'ascending'
                    ? new Date(a[key]) - new Date(b[key])
                    : new Date(b[key]) - new Date(a[key]);
            } else {
                return direction === 'ascending'
                    ? a[key].toString().localeCompare(b[key].toString())
                    : b[key].toString().localeCompare(a[key].toString());
            }
        });

        setFilteredActivities(sortedData);
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? <FaAngleUp /> : <FaAngleDown />;
        }
        return null;
    };

    const fetchActivities = async ({ background = false } = {}) => {
        if (!currentTenant) {
            setIsLoading(false);
            return;
        }

        const tenantId = currentTenant.id;

        if (!background) setIsLoading(true);
        try {
            // Fetch sites for this tenant - may include tenant_site_id
            const sitesResponse = await fetchWithAuth(`${API_BASE_URL}/sites?tenant_id=${tenantId}`);

            // Map site_id to tenant_site_id
            let tenantSites = [];
            if (sitesResponse.ok) {
                const sitesData = await sitesResponse.json();
                
                // The /sites endpoint returns { site_id: site.id, name: site.name }
                // Use site.id as the site_id (which is the site UUID)
                tenantSites = sitesData.map(site => ({
                    site_id: site.id || site.site_id,  // Use id which is the site UUID
                    site_name: site.name || ''
                }));
            }

            // Reuse the sites we just fetched for the filter dropdown, so we don't
            // fire a second /sites request on load.
            setAvailableSites(tenantSites);

            // Now fetch activities for each site using site_id parameter
            const activityMap = new Map(); // activity_key -> activity with sites array
            
            // If we have tenant_sites, fetch activities for each
            if (tenantSites.length > 0) {
                const activityPromises = tenantSites.map(async (ts) => {
                    // Use site_id (not tenant_site_id) - the backend resolves it
                    const response = await fetchWithAuth(`${API_BASE_URL}/tenant-site-activities?tenant_id=${tenantId}&site_id=${ts.site_id}`);
                    
                    if (response.ok) {
                        const data = await response.json();
                        return { tenantSite: ts, activities: Array.isArray(data) ? data : [] };
                    }
                    return { tenantSite: ts, activities: [] };
                });

                const results = await Promise.all(activityPromises);
                
                // Combine all activities - group by activity_key and collect all sites
                results.forEach(({ tenantSite, activities }) => {
                    activities.forEach(item => {
                        const activityKey = item.activity_key;
                        
                        if (activityMap.has(activityKey)) {
                            // Activity already exists, add this site to its sites array
                            const existing = activityMap.get(activityKey);
                            // Add site to sites array if not already present
                            const siteExists = existing.sites.some(s => s.site_id === tenantSite.site_id);
                            if (!siteExists) {
                                existing.sites.push({
                                    site_id: tenantSite.site_id,
                                    site_name: tenantSite.site_name,
                                    tenant_site_id: item.tenant_site_id
                                });
                            }
                        } else {
                            // New activity - create entry
                            // Extract metadata (which may be empty for old activities)
                            const meta = item.metadata || {};
                            
                            // Helper function to get value from flat structure OR nested (backward compatibility)
                            const getMetaValue = (flatKey, nestedPath) => {
                                // Try flat structure first
                                if (flatKey !== undefined && meta[flatKey] !== undefined && meta[flatKey] !== null) {
                                    return meta[flatKey];
                                }
                                // Fall back to nested structure for backward compatibility
                                if (nestedPath) {
                                    const parts = nestedPath.split('.');
                                    let value = meta;
                                    for (const part of parts) {
                                        value = value?.[part];
                                    }
                                    return value;
                                }
                                return null;
                            };
                            
                            activityMap.set(activityKey, {
                                id: item.id,
                                activity_id: item.id,
                                activity_key: item.activity_key,
                                activity_name: item.activity_name,
                                tenant_id: item.tenant_id,
                                tenant_site_id: item.tenant_site_id,
                                metadata: meta,
                                created_at: item.created_at,
                                updated_at: item.updated_at,
                                core_activity_id: item.core_activity_id || null,
                                profile_complete: item.profile_complete === true,
                                incomplete: item.incomplete === true,
                                // Read from flat structure first, fall back to nested for backward compatibility
                                customer_care_type_id: getMetaValue('customer_care_type_id', null) || '',
                                general_category: getMetaValue('general_category', 'catalogue.general_category') || '',
                                frequency: getMetaValue('frequency', 'catalogue.defaults.frequency')?.toString() || '',
                                sn_hours_day: getMetaValue('sn_hours_day', 'catalogue.defaults.sn_hours_day')?.toString() || '0',
                                sn_hours_night: getMetaValue('sn_hours_night', 'catalogue.defaults.sn_hours_night')?.toString() || '0',
                                ena_hours_day: getMetaValue('ena_hours_day', 'catalogue.defaults.ena_hours_day')?.toString() || '0',
                                ena_hours_night: getMetaValue('ena_hours_night', 'catalogue.defaults.ena_hours_night')?.toString() || '0',
                                cw_hours_day: getMetaValue('cw_hours_day', 'catalogue.defaults.cw_hours_day')?.toString() || '0',
                                cw_hours_night: getMetaValue('cw_hours_night', 'catalogue.defaults.cw_hours_night')?.toString() || '0',
                                benefit_body: getMetaValue('benefit_body', 'catalogue.defaults.benefit_body')?.toString() || '',
                                benefit_mind: getMetaValue('benefit_mind', 'catalogue.defaults.benefit_mind')?.toString() || '',
                                benefit_social: getMetaValue('benefit_social', 'catalogue.defaults.benefit_social')?.toString() || '',
                                date_from: getMetaValue('date_from', null) || '',
                                date_to: getMetaValue('date_to', null) || '',
                                // Include site info with tenant_site_id for use in update payloads
                                sites: [{
                                    site_id: tenantSite.site_id,
                                    site_name: tenantSite.site_name,
                                    tenant_site_id: item.tenant_site_id
                                }]
                            });
                        }
                    });
                });
            }

            // Convert map to array
            const uniqueActivities = Array.from(activityMap.values());

            const sorted = [...uniqueActivities].sort((a, b) =>
                a.activity_name?.localeCompare(b.activity_name ?? '')
            );

            setActivities(sorted);
            setPageCache(`activities:${tenantId}`, { activities: sorted, sites: tenantSites });
            // Re-apply current search/site filters instead of resetting to the full list
            filterActivities(searchTerm, selectedSites, sorted);
            if (!sortConfig.key) {
                setSortConfig({ key: 'activity_name', direction: 'ascending' });
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
            setActivities([]);
            setFilteredActivities([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Get unique sites for the filter dropdown
    // Return ALL available tenant sites (not just sites from activities)
    const getUniqueSites = () => {
        return availableSites;
    };

    const handleSearchChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        filterActivities(value, selectedSites);
    };

    const handleSiteFilterChange = (selectedList) => {
        setSelectedSites(selectedList);
        filterActivities(searchTerm, selectedList);
    };

    const filterActivities = (searchValue, siteFilters, baseActivities = null) => {
        let filtered = baseActivities !== null ? baseActivities : activities;

        // Filter by search term (activity name or activity key)
        if (searchValue) {
            filtered = filtered.filter(activity =>
                activity.activity_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                activity.activity_key?.toLowerCase().includes(searchValue.toLowerCase())
            );
        }

        // Filter by selected sites - check against the sites array
        if (siteFilters && siteFilters.length > 0) {
            const selectedSiteIds = siteFilters.map(site => site.site_id);
            filtered = filtered.filter(activity =>
                activity.sites && activity.sites.some(site => selectedSiteIds.includes(site.site_id))
            );
        }

        setFilteredActivities(filtered);
    };

    // Component to display sites as compact badges
    const SitesDisplay = ({ sites }) => {
        if (!sites || sites.length === 0) {
            return <span className="text-muted">-</span>;
        }

        const maxVisible = 2;
        const visibleSites = sites.slice(0, maxVisible);
        const remainingCount = sites.length - maxVisible;
        const tooltipId = `sites-tooltip-${Math.random().toString(36).substr(2, 9)}`;

        return (
            <div className="sites-container">
                {visibleSites.map((site, index) => (
                    <span key={site.site_id} className="site-badge">
                        {site.site_name}
                    </span>
                ))}
                {remainingCount > 0 && (
                    <>
                        <span
                            className="site-badge site-badge-more"
                            data-tooltip-id={tooltipId}
                        >
                            +{remainingCount}
                        </span>
                        <ReactTooltip
                            id={tooltipId}
                            place="top"
                            content={
                                <div style={{ textAlign: 'left' }}>
                                    {sites.slice(maxVisible).map(site => (
                                        <div key={site.site_id}>{site.site_name}</div>
                                    ))}
                                </div>
                            }
                        />
                    </>
                )}
            </div>
        );
    };

    const handleAddActivityClick = () => {
        setAddActivity(true);
        fetchSites();
    };

    const toggleRowCollapse = (activityId) => {
        setCollapsedRows(prevState => ({
            ...prevState,
            [activityId]: !prevState[activityId],
        }));
    };

    // Add validation function for required fields
    const validateRequiredFields = (data, sites) => {
        const errors = {};
        
        // Only validate activity_name as required (activity_key is auto-generated)
        if (!data.activity_name || data.activity_name.toString().trim() === '') {
            errors.activity_name = 'Activity name is required';
        }

        // Validate that at least one site is selected
        if (!sites || sites.length === 0) {
            errors.sites = 'Please select at least one site';
        }

        return errors;
    };

    const handleAddActivity = async () => {
        // Auto-generate activity_key in format "activity_XXX" where XXX is next available number
        // Find the highest activity number from existing activities
        let maxActivityNumber = 0;
        activities.forEach(activity => {
            const match = activity.activity_key?.match(/activity_(\d+)/);
            if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxActivityNumber) {
                    maxActivityNumber = num;
                }
            }
        });
        
        // Next activity number
        const nextActivityNumber = maxActivityNumber + 1;
        const generatedActivityKey = `activity_${nextActivityNumber}`;

        // Check for duplicate activity key within the same tenant
        const activityKeyExists = activities.some(activity =>
            activity.activity_key?.toLowerCase() === generatedActivityKey?.toLowerCase()
        );

        if (activityKeyExists) {
            alert('An activity with this key already exists. Please try again.');
            return;
        }

        // Validate all required fields
        const errors = validateRequiredFields(newActivity, newActivitySites);
        setValidationErrors(errors);

        if (Object.keys(errors).length > 0) {
            const missingFields = Object.keys(errors).map(field => {
                // Convert field names to user-friendly labels
                const fieldLabels = {
                    'activity_name': 'Activity Name',
                    'sites': 'Sites'
                };
                return fieldLabels[field] || field;
            });

            alert(`Please complete the following required fields:\n\n• ${missingFields.join('\n• ')}`);
            return; // Stop execution
        }

        // Check for empty activity name
        if (!newActivity.activity_name.trim()) {
            alert('Activity name cannot be empty.');
            return;
        }

        // Validate benefit values
        const benefitBody = parseFloat(newActivity.benefit_body) || 0;
        const benefitMind = parseFloat(newActivity.benefit_mind) || 0;
        const benefitSocial = parseFloat(newActivity.benefit_social) || 0;

        if (benefitBody < 0 || benefitBody > 100 ||
            benefitMind < 0 || benefitMind > 100 ||
            benefitSocial < 0 || benefitSocial > 100) {
            alert('Benefit values must be between 0 and 100.');
            return;
        }

        try {
            // NEW API: Create one activity record per selected site
            // This ensures each activity is linked to a specific tenant_site_id
            // Defensive: Convert to string before trim to handle non-string types
            const careTypeIdRaw = newActivity.customer_care_type_id;
            const careTypeId = (careTypeIdRaw !== null && careTypeIdRaw !== undefined)
                ? String(careTypeIdRaw).trim()
                : '';
            
            // Store metadata in flat format for backend compatibility
            const metadata = {
                activity: {
                    key: newActivity.activity_key?.trim().toLowerCase().replace(/\s+/g, '_'),
                    name: newActivity.activity_name || ''
                },
                catalogue: {
                    general_category: newActivity.general_category || '',
                    defaults: {
                        frequency: newActivity.frequency || '',
                        sn_hours_day: newActivity.sn_hours_day || '0',
                        sn_hours_night: newActivity.sn_hours_night || '0',
                        ena_hours_day: newActivity.ena_hours_day || '0',
                        ena_hours_night: newActivity.ena_hours_night || '0',
                        cw_hours_day: newActivity.cw_hours_day || '0',
                        cw_hours_night: newActivity.cw_hours_night || '0',
                        benefit_body: newActivity.benefit_body || '',
                        benefit_mind: newActivity.benefit_mind || '',
                        benefit_social: newActivity.benefit_social || ''
                    }
                },
                // FLAT structure - these are at top level for backend compatibility
                general_category: newActivity.general_category || '',
                frequency: newActivity.frequency || '',
                sn_hours_day: newActivity.sn_hours_day || '0',
                sn_hours_night: newActivity.sn_hours_night || '0',
                ena_hours_day: newActivity.ena_hours_day || '0',
                ena_hours_night: newActivity.ena_hours_night || '0',
                cw_hours_day: newActivity.cw_hours_day || '0',
                cw_hours_night: newActivity.cw_hours_night || '0',
                benefit_body: newActivity.benefit_body || '',
                benefit_mind: newActivity.benefit_mind || '',
                benefit_social: newActivity.benefit_social || '',
                customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                date_from: newActivity.date_from || '',
                date_to: newActivity.date_to || ''
            };

            // Create one activity per selected site
            const creationPromises = newActivitySites.map(async (site) => {
                const formattedActivity = {
                    tenant_id: currentTenant?.id,
                    site_id: site.site_id, // Required - the backend resolves to tenant_site_id
                    activity_key: generatedActivityKey,
                    activity_name: newActivity.activity_name || '',
                    metadata: metadata
                };

                const response = await fetchWithAuth(`${API_BASE_URL}/tenant-site-activities`, {
                    method: 'POST',
                    body: JSON.stringify(formattedActivity),
                });

                return response;
            });

            const results = await Promise.all(creationPromises);
            const allSuccessful = results.every(res => res.ok);

            if (!allSuccessful) {
                const errorTexts = await Promise.all(results.map(res => res.text()));
                console.error('Error responses:', errorTexts);
                alert('Some activities could not be created. Please try again.');
                return;
            }

            fetchActivities();
            setAddActivity(false);
            setNewActivity({
                activity_name: '',
                customer_care_type_id: '',
                general_category: '',
                frequency: '',
                sn_hours_day: '',
                sn_hours_night: '',
                ena_hours_day: '',
                ena_hours_night: '',
                cw_hours_day: '',
                cw_hours_night: '',
                benefit_body: '',
                benefit_mind: '',
                benefit_social: '',
                date_from: '',
                date_to: '',
            });
            setNewActivitySites([]);
            setValidationErrors({}); // Clear validation errors
        } catch (error) {
            console.error('Error adding activity:', error);
            alert('An error occurred while creating the activity. Please try again.');
        }
    };

    function debounce(fn, delay) {
        let timeoutID;
        return (...args) => {
            if (timeoutID) {
                clearTimeout(timeoutID);
            }
            timeoutID = setTimeout(() => {
                fn(...args);
            }, delay);
        };
    }
    const handleEditActivityDebounced = debounce(async (activityId, updatedActivity, fetchActivities) => {
        try {
            // Handle customer_care_type_id - keep as comma-separated string for multiple values
            // Defensive: Convert to string before trim to handle non-string types
            const careTypeIdRaw = updatedActivity.customer_care_type_id;
            const careTypeId = (careTypeIdRaw !== null && careTypeIdRaw !== undefined)
                ? String(careTypeIdRaw).trim()
                : '';
            
            // Build metadata object in nested format for the new API
            // Note: For debounced edits, we need to get the key from the activity
            const metadata = {
                activity: {
                    key: updatedActivity.activity_key || '',
                    name: updatedActivity.activity_name
                },
                catalogue: {
                    general_category: updatedActivity.general_category || '',
                    defaults: {
                        frequency: updatedActivity.frequency || '',
                        sn_hours_day: updatedActivity.sn_hours_day || '0',
                        sn_hours_night: updatedActivity.sn_hours_night || '0',
                        ena_hours_day: updatedActivity.ena_hours_day || '0',
                        ena_hours_night: updatedActivity.ena_hours_night || '0',
                        cw_hours_day: updatedActivity.cw_hours_day || '0',
                        cw_hours_night: updatedActivity.cw_hours_night || '0',
                        benefit_body: updatedActivity.benefit_body || '',
                        benefit_mind: updatedActivity.benefit_mind || '',
                        benefit_social: updatedActivity.benefit_social || ''
                    }
                },
                customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                date_from: updatedActivity.date_from || '',
                date_to: updatedActivity.date_to || ''
            };

            const formattedActivity = {
                activity_name: updatedActivity.activity_name || '',
                metadata: metadata
            };

            // Use activity_key (e.g., "activity_612") instead of numeric ID
            const activityKey = updatedActivity.activity_key || activityId;
            const response = await fetchWithAuth(`${API_BASE_URL}/tenant-site-activities/${activityKey}?tenant_id=${currentTenant?.id}`, {
                method: 'PUT',
                body: JSON.stringify(formattedActivity),
            });

            if (response.ok) {
                fetchActivities();
            } else {
                console.error('Failed to update activity');
            }
        } catch (error) {
            console.error('Error updating activity:', error);
        }
    }, 50);



    const [editActivityId, setEditActivityId] = useState(null);
    const [editActivityKey, setEditActivityKey] = useState(null); // Store key for editing
    const [formData, setFormData] = useState({
        activity_name: '',
        customer_care_type_id: '',
        general_category: '',
        frequency: '',
        sn_hours_day: '',
        sn_hours_night: '',
        ena_hours_day: '',
        ena_hours_night: '',
        cw_hours_day: '',
        cw_hours_night: '',
        benefit_body: '',
        benefit_mind: '',
        benefit_social: '',
        date_from: '',
        date_to: '',
    });
    const [editActivitySites, setEditActivitySites] = useState([]);
    const [editingIncomplete, setEditingIncomplete] = useState(false);
    const [editingCoreActivityId, setEditingCoreActivityId] = useState(null);

    const handleEditClick = (activity) => {
        if (activity.incomplete) {
            window.alert('This activity is missing Flow details. Add the frequency, minutes, benefits, dates, and default care types. It cannot be added to a service plan until those are saved.');
        }
        setEditingIncomplete(Boolean(activity.incomplete));
        setEditingCoreActivityId(activity.core_activity_id || null);
        // Use id for new API, activity_id for backward compatibility
        setEditActivityId(activity.id || activity.activity_id || activity.activity_key);
        setEditActivityKey(activity.activity_key || ''); // Store key separately
        setFormData({
            activity_name: activity.activity_name || '',
            customer_care_type_id: activity.customer_care_type_id || '',
            general_category: activity.general_category || '',
            frequency: activity.frequency || '',
            sn_hours_day: activity.sn_hours_day || '',
            sn_hours_night: activity.sn_hours_night || '',
            ena_hours_day: activity.ena_hours_day || '',
            ena_hours_night: activity.ena_hours_night || '',
            cw_hours_day: activity.cw_hours_day || '',
            cw_hours_night: activity.cw_hours_night || '',
            benefit_body: activity.benefit_body || '',
            benefit_mind: activity.benefit_mind || '',
            benefit_social: activity.benefit_social || '',
            date_from: activity.date_from || '',
            date_to: activity.date_to || ''
        });
        // Populate editActivitySites with ALL current activity's sites (not just one)
        const sites = activity.sites || [];
        setEditActivitySites(sites);
        // Fetch sites if not already loaded
        if (availableSites.length === 0) {
            fetchSites();
        }
    };

    const handleSaveClick = async () => {
        const tenantId = currentTenant?.id;

        // Validate that at least one site is selected
        if (!editActivitySites || editActivitySites.length === 0) {
            alert('Please select at least one site');
            return;
        }

        // Validate benefit values
        const benefitBody = parseFloat(formData.benefit_body) || 0;
        const benefitMind = parseFloat(formData.benefit_mind) || 0;
        const benefitSocial = parseFloat(formData.benefit_social) || 0;

        if (benefitBody < 0 || benefitBody > 100 ||
            benefitMind < 0 || benefitMind > 100 ||
            benefitSocial < 0 || benefitSocial > 100) {
            alert('Benefit values must be between 0 and 100.');
            return;
        }

        try {
            // Handle customer_care_type_id - keep as comma-separated string for multiple values
            const careTypeIdRaw = formData.customer_care_type_id;
            const careTypeId = (careTypeIdRaw !== null && careTypeIdRaw !== undefined)
                ? String(careTypeIdRaw).trim()
                : '';
            
            // Build metadata object in flat format (not nested) to match backend expectations
            const metadata = {
                activity: {
                    key: editActivityKey, // Use the stored key
                    name: formData.activity_name
                },
                // Keep catalogue for backward compatibility, but also add flat defaults
                catalogue: {
                    general_category: formData.general_category || '',
                    defaults: {
                        frequency: formData.frequency || '',
                        sn_hours_day: formData.sn_hours_day || '0',
                        sn_hours_night: formData.sn_hours_night || '0',
                        ena_hours_day: formData.ena_hours_day || '0',
                        ena_hours_night: formData.ena_hours_night || '0',
                        cw_hours_day: formData.cw_hours_day || '0',
                        cw_hours_night: formData.cw_hours_night || '0',
                        benefit_body: formData.benefit_body || '',
                        benefit_mind: formData.benefit_mind || '',
                        benefit_social: formData.benefit_social || ''
                    }
                },
                // FLAT structure - these are at top level for backend compatibility
                general_category: formData.general_category || '',
                frequency: formData.frequency || '',
                sn_hours_day: formData.sn_hours_day || '0',
                sn_hours_night: formData.sn_hours_night || '0',
                ena_hours_day: formData.ena_hours_day || '0',
                ena_hours_night: formData.ena_hours_night || '0',
                cw_hours_day: formData.cw_hours_day || '0',
                cw_hours_night: formData.cw_hours_night || '0',
                benefit_body: formData.benefit_body || '',
                benefit_mind: formData.benefit_mind || '',
                benefit_social: formData.benefit_social || '',
                customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                date_from: formData.date_from || '',
                date_to: formData.date_to || ''
            };

            const formattedActivity = {
                activity_name: formData.activity_name || '',
                metadata: metadata
            };

            // Get original sites from the activity being edited
            const originalActivity = activities.find(item => item.activity_key === editActivityKey);
            const originalSites = originalActivity?.sites || [];
            const originalSiteIds = new Set(originalSites.map(s => s.site_id));
            
            // Get the new site IDs selected by user
            const newSiteIds = new Set(editActivitySites.map(s => s.site_id));
            
            // Separate into:
            // - sites to update (exist in both original and new)
            // - sites to create (new, not in original)
            // - sites to delete (in original but not in new - user deselected them)
            const sitesToUpdate = [];
            const sitesToCreate = [];
            const sitesToDelete = [];
            
            editActivitySites.forEach(site => {
                if (originalSiteIds.has(site.site_id)) {
                    sitesToUpdate.push(site);
                } else {
                    sitesToCreate.push(site);
                }
            });
            
            // Find sites that were removed (deselected in the modal)
            originalSites.forEach(site => {
                if (!newSiteIds.has(site.site_id)) {
                    sitesToDelete.push(site);
                }
            });

            const promises = [];
            const completingCoreActivity = editingIncomplete && editingCoreActivityId;

            if (completingCoreActivity) {
                editActivitySites.forEach(site => {
                    const sameSite = site.tenant_site_id && site.tenant_site_id === originalActivity?.tenant_site_id;
                    promises.push(
                        fetchWithAuth(`${API_BASE_URL}/tenant-site-activities`, {
                            method: 'POST',
                            body: JSON.stringify({
                                tenant_id: tenantId,
                                site_id: site.site_id,
                                activity_key: editActivityKey,
                                activity_name: formData.activity_name || '',
                                metadata,
                                core_activity_id: sameSite ? editingCoreActivityId : undefined,
                            }),
                        })
                    );
                });
            }

            if (!completingCoreActivity) {
            // UPDATE existing sites using PUT
            sitesToUpdate.forEach(site => {
                const editSiteId = site?.site_id;
                // Build per-site metadata including tenant_site when we have tenant_site_id
                const siteMetadata = site.tenant_site_id
                    ? { ...metadata, tenant_site: { id: site.tenant_site_id, site_id: site.site_id } }
                    : metadata;
                const siteFormattedActivity = { ...formattedActivity, metadata: siteMetadata };

                // Use activity_key instead of activity ID for the PUT request
                // The backend expects activity_key (e.g., "activity_612") not numeric ID
                const url = editSiteId
                    ? `${API_BASE_URL}/tenant-site-activities/${editActivityKey}?tenant_id=${tenantId}&site_id=${editSiteId}`
                    : `${API_BASE_URL}/tenant-site-activities/${editActivityKey}?tenant_id=${tenantId}`;

                promises.push(
                    fetchWithAuth(url, {
                        method: 'PUT',
                        body: JSON.stringify(siteFormattedActivity),
                    }).then(async res => {
                        const responseText = await res.text();

                        if (!res.ok) {
                            console.error('PUT failed:', res.status, res.statusText);
                        }
                        // Return a mock response object with ok status for testing
                        return { ok: res.ok, status: res.status, statusText: res.statusText, text: () => Promise.resolve(responseText) };
                    })
                );
            });

            // CREATE new sites using POST
            sitesToCreate.forEach(site => {
                const createUrl = `${API_BASE_URL}/tenant-site-activities`;
                const createData = {
                    tenant_id: tenantId,
                    site_id: site.site_id,
                    activity_key: editActivityKey,
                    activity_name: formData.activity_name || '',
                    metadata: metadata
                };

                promises.push(
                    fetchWithAuth(createUrl, {
                        method: 'POST',
                        body: JSON.stringify(createData),
                    })
                );
            });

            // DELETE removed sites using DELETE
            sitesToDelete.forEach(site => {
                const deleteSiteId = site?.site_id;
                if (deleteSiteId) {
                    const deleteParams = new URLSearchParams();
                    deleteParams.append('tenant_id', tenantId);
                    deleteParams.append('site_id', deleteSiteId);
                    
                    // Use activity_key instead of editActivityId (numeric ID)
                    const deleteUrl = `${API_BASE_URL}/tenant-site-activities/${editActivityKey}?${deleteParams.toString()}`;
                    
                    promises.push(
                        fetchWithAuth(deleteUrl, {
                            method: 'DELETE',
                        })
                    );
                }
            });
            }

            const results = await Promise.all(promises);
            const allSuccessful = results.every(res => res.ok);

            if (allSuccessful) {
                fetchActivities();
                setEditActivityId(null);
                setEditingIncomplete(false);
                setEditingCoreActivityId(null);
            } else {
                console.error('Failed to update some activities');
                alert('Some activities could not be updated. Please try again. Check console for details.');
            }
        } catch (error) {
            console.error('Error updating activity:', error);
        }
    };



    const handleDeleteActivity = async (activityKey) => {
        const activityToDelete = activities.find(item => item.activity_key === activityKey);
        if (activityToDelete?.incomplete) {
            window.alert('This activity is missing Flow details. Edit it and save those details. It is not removed from Core.');
            return;
        }
        const tenantId = currentTenant?.id;
        
        // Find the activity to get all its sites
        const activitySites = activityToDelete?.sites || [];
        
        if (activitySites.length === 0) {
            alert('No sites found for this activity');
            return;
        }

        try {
            // Delete from ALL sites
            const deletePromises = activitySites.map(site => {
                const deleteParams = new URLSearchParams();
                deleteParams.append('tenant_id', tenantId);
                deleteParams.append('site_id', site.site_id);
                
                const deleteUrl = `${API_BASE_URL}/tenant-site-activities/${activityKey}?${deleteParams.toString()}`;
                
                return fetchWithAuth(deleteUrl, {
                    method: 'DELETE',
                });
            });

            const results = await Promise.all(deletePromises);
            const allSuccessful = results.every(res => res.ok);

            if (allSuccessful) {
                fetchActivities();
            } else {
                console.error('Failed to delete activity from some sites');
                alert('Could not delete activity from all sites. Please try again.');
            }
        } catch (error) {
            console.error('Error deleting activity:', error);
        }
    };

    const allFilteredKeys = (filteredActivities || []).map(a => a.activity_key);

    const handleSelectActivity = (key, checked) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (checked) next.add(key);
            else next.delete(key);
            return next;
        });
    };

    const handleSelectAll = () => setSelectedKeys(new Set(allFilteredKeys));
    const handleClearSelection = () => setSelectedKeys(new Set());

    const handleBulkEdit = async (changes) => {
        if (Object.keys(changes).length === 0) {
            setShowBulkEditModal(false);
            return;
        }
        setIsBulkEditing(true);
        const keys = Array.from(selectedKeys);
        setBulkEditProgress({ done: 0, total: keys.length });
        let succeeded = 0;
        let failed = 0;
        const tenantId = currentTenant?.id;

        for (const key of keys) {
            const activity = activities.find(a => a.activity_key === key);
            if (!activity) {
                setBulkEditProgress(prev => ({ ...prev, done: prev.done + 1 }));
                continue;
            }

            const merged = {
                general_category: changes.general_category !== undefined ? changes.general_category : (activity.general_category || ''),
                frequency: changes.frequency !== undefined ? changes.frequency : (activity.frequency || ''),
                sn_hours_day: changes.sn_hours_day !== undefined ? changes.sn_hours_day : (activity.sn_hours_day || '0'),
                sn_hours_night: changes.sn_hours_night !== undefined ? changes.sn_hours_night : (activity.sn_hours_night || '0'),
                ena_hours_day: changes.ena_hours_day !== undefined ? changes.ena_hours_day : (activity.ena_hours_day || '0'),
                ena_hours_night: changes.ena_hours_night !== undefined ? changes.ena_hours_night : (activity.ena_hours_night || '0'),
                cw_hours_day: changes.cw_hours_day !== undefined ? changes.cw_hours_day : (activity.cw_hours_day || '0'),
                cw_hours_night: changes.cw_hours_night !== undefined ? changes.cw_hours_night : (activity.cw_hours_night || '0'),
                benefit_body: changes.benefit_body !== undefined ? changes.benefit_body : (activity.benefit_body || ''),
                benefit_mind: changes.benefit_mind !== undefined ? changes.benefit_mind : (activity.benefit_mind || ''),
                benefit_social: changes.benefit_social !== undefined ? changes.benefit_social : (activity.benefit_social || ''),
                customer_care_type_id: changes.customer_care_type_id !== undefined ? changes.customer_care_type_id : (activity.customer_care_type_id || null),
                date_from: changes.date_from !== undefined ? changes.date_from : (activity.date_from || ''),
                date_to: changes.date_to !== undefined ? changes.date_to : (activity.date_to || ''),
            };

            const metadata = {
                activity: { key, name: activity.activity_name },
                catalogue: {
                    general_category: merged.general_category,
                    defaults: {
                        frequency: merged.frequency,
                        sn_hours_day: merged.sn_hours_day,
                        sn_hours_night: merged.sn_hours_night,
                        ena_hours_day: merged.ena_hours_day,
                        ena_hours_night: merged.ena_hours_night,
                        cw_hours_day: merged.cw_hours_day,
                        cw_hours_night: merged.cw_hours_night,
                        benefit_body: merged.benefit_body,
                        benefit_mind: merged.benefit_mind,
                        benefit_social: merged.benefit_social,
                    }
                },
                ...merged,
            };

            const payload = { activity_name: activity.activity_name, metadata };
            const activitySites = activity.sites || [];

            try {
                let results;
                if (changes.sites !== undefined) {
                    const origSiteIds = new Set(activitySites.map(s => s.site_id));
                    const newSites = changes.sites;
                    const newSiteIds = new Set(newSites.map(s => s.site_id));
                    results = await Promise.all([
                        ...activitySites
                            .filter(s => newSiteIds.has(s.site_id))
                            .map(site => fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-activities/${key}?tenant_id=${tenantId}&site_id=${site.site_id}`,
                                { method: 'PUT', body: JSON.stringify(payload) }
                            )),
                        ...newSites
                            .filter(s => !origSiteIds.has(s.site_id))
                            .map(site => fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-activities`,
                                { method: 'POST', body: JSON.stringify({ tenant_id: tenantId, site_id: site.site_id, activity_key: key, activity_name: activity.activity_name, metadata }) }
                            )),
                        ...activitySites
                            .filter(s => !newSiteIds.has(s.site_id))
                            .map(site => fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-activities/${key}?tenant_id=${tenantId}&site_id=${site.site_id}`,
                                { method: 'DELETE' }
                            )),
                    ]);
                } else {
                    results = await Promise.all(
                        activitySites.map(site =>
                            fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-activities/${key}?tenant_id=${tenantId}&site_id=${site.site_id}`,
                                { method: 'PUT', body: JSON.stringify(payload) }
                            )
                        )
                    );
                }
                if (results.every(r => r.ok)) succeeded++;
                else failed++;
            } catch {
                failed++;
            }

            setBulkEditProgress(prev => ({ ...prev, done: prev.done + 1 }));
        }

        setIsBulkEditing(false);
        setShowBulkEditModal(false);
        setSelectedKeys(new Set());

        if (failed > 0) {
            const siteNote = changes.sites !== undefined ? ' One or more site assignments may already exist.' : '';
            alert(`Bulk edit complete. ${succeeded} succeeded, ${failed} failed.${siteNote}`);
        }

        fetchActivities();
    };

    return (
        <div className="activities-container">
            <div className="page-hero">
                <div className="page-hero-text">
                    <h1 className="page-hero-title">Activities</h1>
                    <p className="page-hero-subtitle">View and manage all activities</p>
                </div>
                <div className="page-hero-actions">
                    <div className="page-stat-chip">
                        <div className="page-stat-value">{filteredActivities?.length ?? 0}</div>
                        <div className="page-stat-label">Total</div>
                    </div>
                </div>
            </div>

            <div className="page-toolbar">
                <div className="page-toolbar-left">
                    <Form.Control
                        type="text"
                        placeholder="Search by activity name..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-bar"
                    />
                    <div className="site-filter-dropdown">
                        <Multiselect
                            options={availableSites}
                            displayValue="site_name"
                            selectedValues={selectedSites}
                            onSelect={handleSiteFilterChange}
                            onRemove={handleSiteFilterChange}
                            placeholder="Filter by Sites"
                            {...MULTISELECT_COMMON_PROPS}
                            style={MULTISELECT_THEME_STYLE}
                        />
                    </div>
                </div>
                <div className="page-toolbar-actions">
                    <PermissionGuard
                        permission={PERMISSIONS.ACTIVITY_UPDATE}
                        showDisabled
                        tooltip="You need Administrator permissions to create new Activities"
                    >
                        <Button className="add-activity-btn" style={{ backgroundColor: 'var(--theme-accent)', borderColor: 'var(--theme-accent)' }} onClick={handleAddActivityClick}>Add New Activity</Button>
                    </PermissionGuard>
                </div>
            </div>
            {isLoading && <TableSkeleton columns={10} rows={8} message="Loading activities…" />}
            {!isLoading && (
            <div className="table-controls">
                <Button variant="link" className="btn-accent" onClick={() => toggleRowCollapse('frequency')}>
                    {collapsedRows['frequency'] ? <FaAngleRight /> : <FaAngleLeft />}
                    {collapsedRows['frequency'] ? "Expand Columns" : "Collapse Columns"}
                </Button>
            </div>
            )}
            {!isLoading && (
            <div className="table-wrapper" style={{
                overflowX: 'auto',
                overflowY: 'auto',
                maxHeight: '60vh',
                position: 'relative',
                padding: 0,
                borderTop: '1px solid #f5f5dc'
            }}>
                <Table striped bordered hover style={{
                    position: 'relative',
                    margin: 0,
                    borderCollapse: 'collapse'
                }}>
                    <thead style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: 'var(--theme-table-header-bg)',
                        margin: 0,
                        padding: 0
                    }}>
                        <tr>
                            <th rowSpan={2} style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center', width: '40px', verticalAlign: 'middle' }}>
                                <input
                                    type="checkbox"
                                    checked={allFilteredKeys.length > 0 && allFilteredKeys.every(k => selectedKeys.has(k))}
                                    onChange={e => e.target.checked ? handleSelectAll() : handleClearSelection()}
                                    style={{ cursor: 'pointer' }}
                                />
                            </th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}>Actions</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('activity_name')}>Activity Name{getSortIndicator('activity_name')}</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('customer_care_type_id')}>Default on Service Plans{getSortIndicator('customer_care_type_id')}</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('general_category')}>General Category{getSortIndicator('general_category')}</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}>Sites</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('frequency')}>Frequency{getSortIndicator('frequency')}</th>
                            {collapsedRows['frequency'] ? null : (
                                <>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} colSpan={2}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Minutes" /></th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} colSpan={2}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Minutes" /></th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} colSpan={2}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Minutes" /></th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} colSpan={3}>Benefit %</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('date_from', true)}>From{getSortIndicator('date_from')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('date_to', true)}>To{getSortIndicator('date_to')}</th>
                                </>
                            )}
                        </tr>

                        <tr>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            {collapsedRows['frequency'] ? null : (
                                <>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('sn_hours_day', true)}>Day{getSortIndicator('sn_hours_day')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('sn_hours_night', true)}>Night{getSortIndicator('sn_hours_night')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('ena_hours_day')}>Day{getSortIndicator('ena_hours_day')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('ena_hours_night')}>Night{getSortIndicator('ena_hours_night')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('cw_hours_day')}>Day{getSortIndicator('cw_hours_day')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('cw_hours_night')}>Night{getSortIndicator('cw_hours_night')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('benefit_body')}>Body{getSortIndicator('benefit_body')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('benefit_mind')}>Mind{getSortIndicator('benefit_mind')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('benefit_social')}>Social{getSortIndicator('benefit_social')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {(filteredActivities || []).map(activity => (
                            <tr key={activity.activity_key || activity.id || activity.activity_id} style={selectedKeys.has(activity.activity_key) ? { backgroundColor: 'var(--theme-table-row-selected)' } : {}}>
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedKeys.has(activity.activity_key)}
                                        onChange={e => handleSelectActivity(activity.activity_key, e.target.checked)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </td>
                                <td style={{ padding: 0 }}>
                                    <div className="actions-cell">
                                        <PermissionGuard
                                            permission={PERMISSIONS.ACTIVITY_UPDATE}
                                            showDisabled
                                            tooltip="You don't have permission to edit Activities"
                                        >
                                            <Button variant="link" data-tooltip-id={`edit-tooltip-${activity.id || activity.activity_id}`}>
                                                <FaEdit
                                                    style={{ color: '#007bff', fontSize: '1.2rem' }}
                                                    onClick={() => handleEditClick(activity)}
                                                />
                                            </Button>
                                        </PermissionGuard>
                                        <ReactTooltip style={{ zIndex: 1000 }} id={`edit-tooltip-${activity.id || activity.activity_id}`} place="top" content="Edit Activity" />

                                        <PermissionGuard 
                                            permission={PERMISSIONS.ACTIVITY_DELETE}
                                            showDisabled
                                            tooltip="You don't have permission to delete Activities"
                                        >
                                            <Button variant="link" data-tooltip-id={`delete-tooltip-${activity.activity_key}`}>
                                                <FaTrash
                                                    style={{ color: '#dc3545', fontSize: '1.2rem' }}
                                                    onClick={() => {
                                                        if (window.confirm("Are you sure you want to remove this item?")) {
                                                            handleDeleteActivity(activity.activity_key)
                                                        }
                                                    }}
                                                />
                                            </Button>
                                        </PermissionGuard>
                                        <ReactTooltip style={{ zIndex: 1000 }} id={`delete-tooltip-${activity.activity_key}`} place="top" content="Delete Activity" />
                                    </div>
                                </td>
                                <td>
                                    {activity.activity_name}
                                    {activity.incomplete ? (
                                        <span className="badge bg-warning text-dark ms-2">Incomplete</span>
                                    ) : null}
                                </td>
                                <td>{(activity.customer_care_type_id !== null && activity.customer_care_type_id !== undefined)
                                    ? String(activity.customer_care_type_id).split(',').map(id => {
                                        const careType = customerCareTypes.find(type => type?.id?.toString() === id?.trim());
                                        return careType ? careType.name : id?.trim();
                                    }).filter(Boolean).join(', ')
                                    : ''}</td>
                                <td>{activity.general_category}</td>
                                <td><SitesDisplay sites={activity.sites || []} /></td>
                                <td>{activity.frequency}</td>
                                {collapsedRows['frequency'] ? null : (
                                    <>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(activity.sn_hours_day)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(activity.sn_hours_night)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(activity.ena_hours_day)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(activity.ena_hours_night)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(activity.cw_hours_day)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(activity.cw_hours_night)}</td>
                                        <td>{activity.benefit_body}</td>
                                        <td>{activity.benefit_mind}</td>
                                        <td>{activity.benefit_social}</td>
                                        <td>{activity.date_from ? new Date(activity.date_from).toISOString().split('T')[0] : ''}</td>
                                        <td>{activity.date_to ? new Date(activity.date_to).toISOString().split('T')[0] : ''}</td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            )}

            <Modal show={AddActivity} onHide={() => {
                setAddActivity(false);
                setValidationErrors({}); // Clear validation errors when modal is closed
            }}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Activity</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="row">
                            {/* Left Column */}
                            <div className="col-md-6">
                                <Form.Group controlId="formActivityName" className="mb-3">
                                    <Form.Label>Activity Name <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newActivity.activity_name}
                                        onChange={(e) => {
                                            setNewActivity({ ...newActivity, activity_name: e.target.value });
                                            if (validationErrors.activity_name) {
                                                setValidationErrors(prev => ({ ...prev, activity_name: null }));
                                            }
                                        }}
                                        isInvalid={!!validationErrors.activity_name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.activity_name}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId="formCustomerCareTypeID" className="mb-3">
                                    <Form.Label>Default on Service Plans</Form.Label>
                                    <MultiselectWithSelectAll
                                        options={customerCareTypes}
                                        displayValue="name"
                                        selectedValues={
                                            customerCareTypes.filter(item =>
                                                item?.id && newActivity.customer_care_type_id !== null && newActivity.customer_care_type_id !== undefined &&
                                                String(newActivity.customer_care_type_id).split(", ").includes(item.id.toString())
                                            )
                                        }
                                        onSelect={(selectedList) => {
                                            const selectedIds = selectedList.map(item => item.id).join(", ");
                                            setNewActivity(prev => ({ ...prev, customer_care_type_id: selectedIds }));
                                        }}
                                        onRemove={(selectedList) => {
                                            const selectedIds = selectedList.map(item => item.id).join(", ");
                                            setNewActivity(prev => ({ ...prev, customer_care_type_id: selectedIds }));
                                        }}
                                        placeholder="Select service types"
                                    />
                                </Form.Group>

                                <Form.Group controlId="formgeneral_category" className="mb-3">
                                    <Form.Label>General Category</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={newActivity.general_category}
                                        onChange={(e) => setNewActivity({ ...newActivity, general_category: e.target.value })}
                                    >
                                        <option value="">Select a category</option>
                                        {GENERAL_CATEGORY_OPTIONS.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>

                                <Form.Group controlId="formfrequency" className="mb-3">
                                    <Form.Label>Frequency</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newActivity.frequency}
                                        onChange={(e) => setNewActivity({ ...newActivity, frequency: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group controlId="formSites" className="mb-3">
                                    <Form.Label>Sites <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <MultiselectWithSelectAll
                                        options={availableSites}
                                        displayValue="site_name"
                                        selectedValues={newActivitySites}
                                        onSelect={(selectedList) => {
                                            setNewActivitySites(selectedList);
                                            if (validationErrors.sites) {
                                                setValidationErrors(prev => ({ ...prev, sites: null }));
                                            }
                                        }}
                                        onRemove={(selectedList) => setNewActivitySites(selectedList)}
                                        placeholder="Select Sites"
                                        style={{
                                            ...MULTISELECT_THEME_STYLE,
                                            ...(validationErrors.sites ? {
                                                searchBox: { ...MULTISELECT_THEME_STYLE.searchBox, border: '1px solid #dc3545' },
                                                optionContainer: { ...MULTISELECT_THEME_STYLE.optionContainer, border: '1px solid #dc3545' }
                                            } : {})
                                        }}
                                    />
                                    {validationErrors.sites && (
                                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                            {validationErrors.sites}
                                        </div>
                                    )}
                                </Form.Group>
                            </div>

                            {/* Right Column */}
                            <div className="col-md-6">
                                {/* Minutes Section */}
                                <div className="mb-3">
                                    <h6 style={FORM_SECTION_TITLE_STYLE}>Minutes</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formsn_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('SN')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newActivity.sn_hours_day}
                                                    onChange={(e) => setNewActivity({ ...newActivity, sn_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formsn_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('SN')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newActivity.sn_hours_night}
                                                    onChange={(e) => setNewActivity({ ...newActivity, sn_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formena_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('ENA')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newActivity.ena_hours_day}
                                                    onChange={(e) => setNewActivity({ ...newActivity, ena_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formena_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('ENA')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newActivity.ena_hours_night}
                                                    onChange={(e) => setNewActivity({ ...newActivity, ena_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formcw_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('CW')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newActivity.cw_hours_day}
                                                    onChange={(e) => setNewActivity({ ...newActivity, cw_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formcw_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('CW')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newActivity.cw_hours_night}
                                                    onChange={(e) => setNewActivity({ ...newActivity, cw_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Benefits Section */}
                                <div className="mb-3">
                                    <h6 style={FORM_SECTION_TITLE_STYLE}>Benefits</h6>
                                    <div className="row">
                                        <div className="col-4">
                                            <Form.Group controlId="formbenefit_body" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Body %</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    max="100"
                                                    value={newActivity.benefit_body}
                                                    onChange={(e) => { const n = parseFloat(e.target.value); setNewActivity({ ...newActivity, benefit_body: !isNaN(n) && n > 100 ? '100' : !isNaN(n) && n < 0 ? '0' : e.target.value }); }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-4">
                                            <Form.Group controlId="formbenefit_mind" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Mind %</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    max="100"
                                                    value={newActivity.benefit_mind}
                                                    onChange={(e) => { const n = parseFloat(e.target.value); setNewActivity({ ...newActivity, benefit_mind: !isNaN(n) && n > 100 ? '100' : !isNaN(n) && n < 0 ? '0' : e.target.value }); }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-4">
                                            <Form.Group controlId="formbenefit_social" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Social %</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    max="100"
                                                    value={newActivity.benefit_social}
                                                    onChange={(e) => { const n = parseFloat(e.target.value); setNewActivity({ ...newActivity, benefit_social: !isNaN(n) && n > 100 ? '100' : !isNaN(n) && n < 0 ? '0' : e.target.value }); }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Dates Section */}
                                <div className="mb-3">
                                    <h6 style={FORM_SECTION_TITLE_STYLE}>Valid Period</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formdate_from" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Date From</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={newActivity.date_from}
                                                    onChange={(e) => setNewActivity({ ...newActivity, date_from: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formdate_to" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Date To</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={newActivity.date_to}
                                                    onChange={(e) => setNewActivity({ ...newActivity, date_to: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-4">
                            <Button className="btn-primary" style={{ backgroundColor: 'var(--theme-accent)', borderColor: 'var(--theme-accent)' }} onClick={handleAddActivity}>Add Activity</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={editActivityId !== null} onHide={() => { setEditActivityId(null); setEditingIncomplete(false); setEditingCoreActivityId(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingIncomplete ? 'Complete activity' : 'Edit Activity'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingIncomplete ? (
                        <div className="alert alert-warning py-2" role="alert">
                            Add the frequency, minutes, benefits, dates, and default care types. This activity stays off service plans until you save.
                        </div>
                    ) : null}
                    <Form>
                        <div className="row">
                            {/* Left Column */}
                            <div className="col-md-6">
                                <Form.Group controlId="formEditActivityName" className="mb-3">
                                    <Form.Label>Activity Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.activity_name}
                                        onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group controlId="formEditCustomerCareTypeID" className="mb-3">
                                    <Form.Label>Default on Service Plans</Form.Label>
                                    <MultiselectWithSelectAll
                                        options={customerCareTypes}
                                        displayValue="name"
                                        selectedValues={
                                            customerCareTypes.filter(item =>
                                                item?.id && formData.customer_care_type_id !== null && formData.customer_care_type_id !== undefined &&
                                                String(formData.customer_care_type_id).split(", ").includes(item.id.toString())
                                            )
                                        }
                                        onSelect={(selectedList) => {
                                            const selectedIds = selectedList.map(item => item.id).join(", ");
                                            setFormData(prev => ({ ...prev, customer_care_type_id: selectedIds }));
                                        }}
                                        onRemove={(selectedList) => {
                                            const selectedIds = selectedList.map(item => item.id).join(", ");
                                            setFormData(prev => ({ ...prev, customer_care_type_id: selectedIds }));
                                        }}
                                        placeholder="Select service types"
                                    />
                                </Form.Group>

                                <Form.Group controlId="formEditgeneral_category" className="mb-3">
                                    <Form.Label>General Category</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={formData.general_category}
                                        onChange={(e) => setFormData({ ...formData, general_category: e.target.value })}
                                    >
                                        <option value="">Select a category</option>
                                        {GENERAL_CATEGORY_OPTIONS.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>

                                <Form.Group controlId="formEditfrequency" className="mb-3">
                                    <Form.Label>Frequency</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.frequency}
                                        onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group controlId="formEditSites" className="mb-3">
                                    <Form.Label>Sites <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <MultiselectWithSelectAll
                                        options={availableSites}
                                        displayValue="site_name"
                                        selectedValues={editActivitySites}
                                        onSelect={(selectedList) => setEditActivitySites(selectedList)}
                                        onRemove={(selectedList) => setEditActivitySites(selectedList)}
                                        placeholder="Select Sites"
                                    />
                                </Form.Group>
                            </div>

                            {/* Right Column */}
                            <div className="col-md-6">
                                {/* Minutes Section */}
                                <div className="mb-3">
                                    <h6 style={FORM_SECTION_TITLE_STYLE}>Minutes</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditsn_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('SN')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.sn_hours_day}
                                                    onChange={(e) => setFormData({ ...formData, sn_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditsn_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('SN')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.sn_hours_night}
                                                    onChange={(e) => setFormData({ ...formData, sn_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditena_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('ENA')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.ena_hours_day}
                                                    onChange={(e) => setFormData({ ...formData, ena_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditena_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('ENA')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.ena_hours_night}
                                                    onChange={(e) => setFormData({ ...formData, ena_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditcw_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('CW')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.cw_hours_day}
                                                    onChange={(e) => setFormData({ ...formData, cw_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditcw_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('CW')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.cw_hours_night}
                                                    onChange={(e) => setFormData({ ...formData, cw_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Benefits Section */}
                                <div className="mb-3">
                                    <h6 style={FORM_SECTION_TITLE_STYLE}>Benefits</h6>
                                    <div className="row">
                                        <div className="col-4">
                                            <Form.Group controlId="formEditbenefit_body" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Body %</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    max="100"
                                                    value={formData.benefit_body}
                                                    onChange={(e) => { const n = parseFloat(e.target.value); setFormData({ ...formData, benefit_body: !isNaN(n) && n > 100 ? '100' : !isNaN(n) && n < 0 ? '0' : e.target.value }); }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-4">
                                            <Form.Group controlId="formEditbenefit_mind" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Mind %</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    max="100"
                                                    value={formData.benefit_mind}
                                                    onChange={(e) => { const n = parseFloat(e.target.value); setFormData({ ...formData, benefit_mind: !isNaN(n) && n > 100 ? '100' : !isNaN(n) && n < 0 ? '0' : e.target.value }); }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-4">
                                            <Form.Group controlId="formEditbenefit_social" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Social %</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    max="100"
                                                    value={formData.benefit_social}
                                                    onChange={(e) => { const n = parseFloat(e.target.value); setFormData({ ...formData, benefit_social: !isNaN(n) && n > 100 ? '100' : !isNaN(n) && n < 0 ? '0' : e.target.value }); }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Dates Section */}
                                <div className="mb-3">
                                    <h6 style={FORM_SECTION_TITLE_STYLE}>Valid Period</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditdate_from" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Date From</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={formData.date_from}
                                                    onChange={(e) => setFormData({ ...formData, date_from: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditdate_to" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Date To</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={formData.date_to}
                                                    onChange={(e) => setFormData({ ...formData, date_to: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-4">
                            <Button variant="primary" onClick={handleSaveClick}>Save Changes</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <BulkEditActivitiesModal
                show={showBulkEditModal}
                onHide={() => setShowBulkEditModal(false)}
                selectedCount={selectedKeys.size}
                customerCareTypes={customerCareTypes}
                availableSites={availableSites}
                onApply={handleBulkEdit}
                isApplying={isBulkEditing}
                progress={bulkEditProgress}
            />

            {selectedKeys.size > 0 && (
                <div style={{
                    position: 'fixed',
                    bottom: '24px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1f2937',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                    zIndex: 1050,
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                }}>
                    <span style={{ fontWeight: 600 }}>{selectedKeys.size} selected</span>
                    <span style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', alignSelf: 'stretch' }} />
                    <Button
                        variant="link"
                        style={{ color: 'rgba(255,255,255,0.8)', padding: 0, fontSize: '13px', textDecoration: 'underline' }}
                        onClick={handleSelectAll}
                    >
                        Select all ({allFilteredKeys.length})
                    </Button>
                    <span style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', alignSelf: 'stretch' }} />
                    <PermissionGuard
                        permission={PERMISSIONS.ACTIVITY_UPDATE}
                        showDisabled
                        tooltip="You need update permissions to bulk edit Activities"
                    >
                        <Button
                            variant="light"
                            size="sm"
                            onClick={() => setShowBulkEditModal(true)}
                        >
                            Edit fields
                        </Button>
                    </PermissionGuard>
                    <Button
                        variant="link"
                        style={{ color: 'rgba(255,255,255,0.6)', padding: 0, fontSize: '16px', lineHeight: 1 }}
                        onClick={handleClearSelection}
                    >
                        <FaTimesCircle />
                    </Button>
                </div>
            )}

        </div>
    );
}

export default Activities;
